import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import PageFooter from "@/components/landing/PageFooter";

/**
 * Szolgáltatások — központi áttekintő oldal.
 *
 * Nem duplikál tartalmat: minden „Részletek" gomb a honlapon már meglévő
 * részletes bemutatáshoz vezet (anchor-ral a Forrás-Kód® Kísérés oldalon).
 */
const SERVICES = [
  {
    id: "egyeni-kiseres",
    tag: "Egyéni kísérés",
    titleLine1: "Személyes",
    titleAccent: "folyamat",
    description:
      "Személyre szabott folyamat, amelyben lehetőséged nyílik mélyebben rálátni saját működésedre, felismerni az összefüggéseket, és új lehetőségeket megnyitni az életedben.",
    linkTo: "/forras-kod-kiseres#egyeni-kiseres",
    testId: "service-egyeni",
  },
  {
    id: "csoportos-kiseres",
    tag: "Csoportos kísérés",
    titleLine1: "Közös",
    titleAccent: "térben",
    description:
      "Közös térben zajló folyamatok, ahol az egyéni felismerések és a csoportos kapcsolódás egymást támogatva nyithatnak új lehetőségeket.",
    linkTo: "/forras-kod-kiseres#csoportos-kiseres",
    testId: "service-csoportos",
  },
  {
    id: "elmenyprogram",
    tag: "Élményprogramok",
    titleLine1: "Egy- vagy",
    titleAccent: "többnapos",
    description:
      "Intenzívebb, élményalapú folyamatok, amelyek lehetőséget adnak a mélyebb megtapasztalásra, új minőségek felfedezésére és azok mindennapi életbe történő beépítésére.",
    linkTo: "/elmenyprogram",
    testId: "service-elmenyprogram",
  },
];

export default function Szolgaltatasok() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") || null;

    document.title = "Szolgáltatások | Szász-Tóth Enikő";
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Egyéni kísérés, csoportos kísérés és élményprogramok Szász-Tóth Enikővel — egy helyen, elegáns áttekintésben."
      );
    }
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc != null) metaDesc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main
      data-testid="szolgaltatasok-page"
      className="bg-warm text-graphite min-h-screen"
    >
      <Header />
      <div className="pt-20 md:pt-24" />

      {/* HERO */}
      <section
        data-testid="szolgaltatasok-hero"
        className="px-6 md:px-10 pt-6 md:pt-10 pb-6 md:pb-8"
      >
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-body text-[11px] md:text-[12px] tracking-[0.42em] uppercase text-gold mb-5">
            — Szolgáltatások
          </p>
          <h1
            data-testid="szolgaltatasok-title"
            className="font-serif-display text-[36px] sm:text-[44px] md:text-[52px] leading-[1.12] text-graphite tracking-tight mb-6"
          >
            Szolgál
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              tatások
            </em>
          </h1>
          <p className="font-serif-display italic text-graphite text-[18px] md:text-[20px] leading-[1.65] max-w-xl mx-auto">
            Három lehetőség a{" "}
            <span className="text-gold">belső utadon</span>.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section
        data-testid="services-grid"
        className="px-5 md:px-10 pb-14 md:pb-20"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {SERVICES.map((s) => (
              <ServiceCard key={s.id} service={s} />
            ))}
          </div>
        </div>
      </section>

      <PageFooter />
    </main>
  );
}

/* ------------------------------------------------------------------- */
/* ServiceCard — unified visual language, equal-height, premium border  */
/* ------------------------------------------------------------------- */
function ServiceCard({ service }) {
  return (
    <article
      data-testid={service.testId}
      className="bg-warm border border-hairline hover:border-gold transition-colors duration-500 px-7 md:px-9 py-10 md:py-12 flex flex-col h-full text-center"
      style={{ boxShadow: "0 10px 40px -28px rgba(160, 122, 40, 0.22)" }}
    >
      <p className="font-body text-[10.5px] md:text-[11px] tracking-[0.35em] uppercase text-gold mb-5">
        — {service.tag}
      </p>

      <h2 className="font-serif-display text-[26px] sm:text-[30px] md:text-[32px] leading-[1.2] text-graphite mb-8 md:mb-10">
        {service.titleLine1}
        <br />
        <em className="text-gold" style={{ fontStyle: "italic" }}>
          {service.titleAccent}
        </em>
      </h2>

      <p className="font-body font-light text-graphite text-[15.5px] md:text-[16px] leading-[1.8] mb-10 md:mb-12 max-w-sm mx-auto">
        {service.description}
      </p>

      {/* Spacer pushes CTA to the bottom so all three align */}
      <div className="flex-grow min-h-[8px]" aria-hidden="true" />

      <div className="pt-6 border-t border-hairline">
        <Link
          to={service.linkTo}
          data-testid={`${service.testId}-cta`}
          className="cta-gold-outline inline-flex items-center gap-3 px-8 py-3.5 text-[11.5px] md:text-[12px] uppercase font-body font-medium tracking-[0.22em]"
        >
          Részletek
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}
